/**
 * Web component: Anchor section (anchor-section)
 * -----------------------------------------------------------------------------
 * Section used for anchor linking, can be added on any page.
 * - Component settings: section / limited / web / vanilla
 *
 */
import './anchor-section.scss';

import { vueReady } from '~/core/runners';
import useIndexStore from '~stores/index';

/**
 * Create new <anchor-section> custom element.
 */
class AnchorSection extends HTMLElement {
  data() {
    return {
      scrollElement: this.getAttribute('data-scroll'),
      desktopMargin: this.getAttribute('data-desktop-margin'),
      mobileMargin: this.getAttribute('data-mobile-margin'),
    };
  }

  constructor() {
    super();
    this.$data = this.data(this);
  }

  /**
   * Scroll to the target section, accounting for header height and margin.
   */
  scrollToTarget() {
    const isMobile = useIndexStore().screen.mobile;
    const margin =
      Number.parseInt(
        isMobile ? this.$data.mobileMargin : this.$data.desktopMargin,
      ) || 0;

    const header = document.querySelector('.site-header-group');
    const headerHeight = header ? header.clientHeight : 0;

    const target = this.parentElement?.nextElementSibling;
    if (!target) return;

    const offset = headerHeight + margin;
    const scrollTolerance = 20;
    const rect = target.getBoundingClientRect();

    const prefersReduced =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    const behavior = prefersReduced ? 'auto' : 'smooth';

    if (Math.abs(rect.top - offset) <= scrollTolerance) return;

    const scrollToSection = window.scrollY + rect.top - offset;
    try {
      window.scrollTo({ top: scrollToSection, behavior });
    } catch {
      window.scrollTo(0, scrollToSection);
    }
  }

  /**
   * Lifecycle hook - component is attached to DOM.
   */
  connectedCallback() {
    const urlHash = window.location.hash;
    if (urlHash !== '' && urlHash === this.$data.scrollElement) {
      // waiting until everything is loaded on initial page load
      setTimeout(() => this.scrollToTarget(), 1000);
    }

    this.onHashChange = () => {
      if (window.location.hash === this.$data.scrollElement) {
        this.scrollToTarget();
      }
    };

    this.onAnchorClick = (event) => {
      const link = event.target.closest('a[href], button[data-href]');
      if (!link) return;

      const href = link.getAttribute('href') || link.getAttribute('data-href');
      if (href !== this.$data.scrollElement) return;

      event.preventDefault();
      history.pushState(null, '', href);
      this.scrollToTarget();
    };

    window.addEventListener('hashchange', this.onHashChange);
    this.addEventListener('click', this.onAnchorClick);
  }

  /**
   * Lifecycle hook - component is removed from DOM.
   */
  disconnectedCallback() {
    window.removeEventListener('hashchange', this.onHashChange);
    this.removeEventListener('click', this.onAnchorClick);
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('anchor-section')) {
    return;
  }

  customElements.define('anchor-section', AnchorSection);
});
